import { SECTION, SECTION_H } from "#puf/constants.ts";
import { store } from "#puf/store/index.ts";
import { collectStateIds, type PxpfTransition } from "@pixelpuf/schemas/pxpf";
import { frameRange } from "@pixelpuf/schemas/state";
import { useSelector } from "@xstate/store-react";

/**
 * Right-hand panel of the machine editor. Renders one of three states based on
 * what's selected: a state, a transition, or nothing. Forms write back to the
 * store via dedicated events so undo treats each edit atomically.
 */
export function PropertiesPanel() {
  const selectedStateId = useSelector(store, (s) => s.context.selectedStateId);
  const selectedTransitionIndex = useSelector(store, (s) => s.context.selectedTransitionIndex);

  if (selectedStateId !== null) return <StateForm stateId={selectedStateId} />;
  if (selectedTransitionIndex !== null) return <TransitionForm transitionIndex={selectedTransitionIndex} />;
  return <Empty />;
}

function Empty() {
  return (
    <aside className="border-t border-line bg-bg-2 lg:border-t-0 lg:border-l lg:overflow-y-auto">
      <div className={SECTION}>
        <h3 className={SECTION_H}>Properties</h3>
        <p className="text-dim text-[11px]">Click a state or transition to edit its properties.</p>
      </div>
    </aside>
  );
}

function StateForm({ stateId }: { stateId: string }) {
  const machine = useSelector(store, (s) => s.context.stateMachine);
  const range = useSelector(store, (s) => s.context.stateFrames[stateId]);
  const frameCount = useSelector(store, (s) => s.context.frames.length);

  if (!machine) return <Empty />;
  const isInitial = machine.initial === stateId;

  function onRename(newId: string) {
    if (!newId || newId === stateId) return;
    if (machine && collectStateIds(machine).has(newId)) return; // collision
    store.trigger["state.rename"]({ oldId: stateId, newId });
    store.trigger["editor.selectState"]({ id: newId });
  }

  function onSetRange(part: 0 | 1, value: number) {
    // The number input's `max` attribute is advisory — keyboard typing can
    // exceed it. Clamp to [0, frameCount-1] before constructing the range so
    // playback never references a frame that doesn't exist.
    const max = Math.max(0, frameCount - 1);
    const v = Math.max(0, Math.min(max, Math.floor(value)));
    const [s, e] = range ?? frameRange(0, 0);
    const next = part === 0 ? frameRange(v, Math.max(v, e)) : frameRange(Math.min(s, v), v);
    store.trigger["stateFrames.upsert"]({ id: stateId, range: next });
  }

  return (
    <aside className="border-t border-line bg-bg-2 lg:border-t-0 lg:border-l lg:overflow-y-auto">
      <div className={SECTION}>
        <h3 className={SECTION_H}>State</h3>
        <label className="block text-[10px] tracking-[1.4px] text-dim mb-1 uppercase">Id</label>
        <input
          type="text"
          defaultValue={stateId}
          onBlur={(e) => {
            onRename(e.target.value.trim());
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          className="w-full bg-bg-3 border border-line text-text px-2 py-1 rounded-sm text-xs font-mono"
        />
        <div className="flex items-center gap-2 mt-2">
          <input
            type="checkbox"
            id={`init-${stateId}`}
            checked={isInitial}
            onChange={() => {
              if (!isInitial) store.trigger["state.setInitial"]({ id: stateId });
            }}
            disabled={isInitial}
            className="cursor-pointer"
          />
          <label htmlFor={`init-${stateId}`} className="text-[11px] text-dim cursor-pointer select-none">
            Initial state
          </label>
        </div>
      </div>
      <div className={SECTION}>
        <h3 className={SECTION_H}>Frame range</h3>
        <div className="flex items-center gap-2 text-[11px]">
          <input
            type="number"
            min={0}
            max={Math.max(0, frameCount - 1)}
            value={range?.[0] ?? 0}
            onChange={(e) => {
              onSetRange(0, Number(e.target.value));
            }}
            className="w-16 bg-bg-3 border border-line text-text px-1.5 py-0.5 rounded-[3px]"
          />
          <span className="text-dim">→</span>
          <input
            type="number"
            min={0}
            max={Math.max(0, frameCount - 1)}
            value={range?.[1] ?? 0}
            onChange={(e) => {
              onSetRange(1, Number(e.target.value));
            }}
            className="w-16 bg-bg-3 border border-line text-text px-1.5 py-0.5 rounded-[3px]"
          />
          <span className="text-dim">/ {frameCount} frames</span>
        </div>
      </div>
    </aside>
  );
}

function TransitionForm({ transitionIndex }: { transitionIndex: number }) {
  const machine = useSelector(store, (s) => s.context.stateMachine);
  if (!machine) return <Empty />;
  const transition = machine.transitions[transitionIndex];
  if (!transition) return <Empty />;
  const index = transitionIndex;

  const stateIds = [...collectStateIds(machine)];

  function onPatch(patch: Partial<PxpfTransition>) {
    store.trigger["transition.update"]({ index, patch });
  }

  return (
    <aside className="border-t border-line bg-bg-2 lg:border-t-0 lg:border-l lg:overflow-y-auto">
      <div className={SECTION}>
        <h3 className={SECTION_H}>Transition</h3>
        <label className="block text-[10px] tracking-[1.4px] text-dim mb-1 uppercase mt-2">From</label>
        <div className="text-xs font-mono text-dim">{transition.from} (read-only)</div>

        <label className="block text-[10px] tracking-[1.4px] text-dim mb-1 uppercase mt-3">Event</label>
        <input
          type="text"
          defaultValue={transition.event}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && v !== transition.event) onPatch({ event: v });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          className="w-full bg-bg-3 border border-line text-text px-2 py-1 rounded-sm text-xs font-mono"
        />

        <label className="block text-[10px] tracking-[1.4px] text-dim mb-1 uppercase mt-3">To</label>
        <select
          value={transition.to}
          onChange={(e) => {
            onPatch({ to: e.target.value });
          }}
          className="w-full bg-bg-3 border border-line text-text px-2 py-1 rounded-sm text-xs font-mono"
        >
          {stateIds.map((id) => <option key={id} value={id}>{id}</option>)}
        </select>

        <label className="block text-[10px] tracking-[1.4px] text-dim mb-1 uppercase mt-3">Trigger</label>
        <select
          value={transition.trigger}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "manual" || v === "onLoopEnd") onPatch({ trigger: v });
          }}
          className="w-full bg-bg-3 border border-line text-text px-2 py-1 rounded-sm text-xs font-mono"
        >
          <option value="manual">manual</option>
          <option value="onLoopEnd">onLoopEnd</option>
        </select>
      </div>
    </aside>
  );
}
